import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation } from 'react-query'
import Stars02 from '@untitled-ui/icons-react/build/esm/Stars02'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import { IconButton } from '@clickguard/design-system'
import { useUserStore } from 'store'
import { accountOverviewApi } from 'services/account-overview/account-overview'
import { CoachmarkTextButton } from './components/coachmark-text-button'
import { isAccountOverviewDashboardPath } from '../account-overview-dashboard'

type Props = {
	/** Opens the Account Overview page (also marks the intro seen). */
	onExplore: () => void
}

/**
 * DEV-2339 · T12 — First-time discovery coachmark.
 *
 * A one-time popover anchored under the navbar monitor icon (caret on top pointing
 * up at the icon). Shows only when the user lands on the Dashboard and has never
 * opened Account Overview (`user.account_overview_intro_seen` false — T11). Any
 * dismissal (X / Got it / Explore) — or opening the AO page by any route — persists
 * the flag so it never shows again.
 *
 * Rendered inside a `relative` container wrapping the monitor icon in `navbar.tsx`.
 */
export const AccountOverviewCoachmark = ({ onExplore }: Props) => {
	const location = useLocation()
	const user = useUserStore((s) => s.user)
	const setUser = useUserStore((s) => s.setUser)
	const [dismissed, setDismissed] = useState(false)

	const { mutate: markIntroSeen } = useMutation(() =>
		accountOverviewApi.markIntroSeen()
	)

	const seen = user?.account_overview_intro_seen ?? false
	const visible = !dismissed && !seen && isAccountOverviewDashboardPath(location.pathname)

	// Persist the flag (server + local store) so it never re-shows this/next session.
	const dismiss = () => {
		setDismissed(true)
		if (user) setUser({ ...user, account_overview_intro_seen: true })
		markIntroSeen()
	}

	if (!visible) return null

	return (
		<div
			role="dialog"
			aria-label="Account overview introduction"
			className="absolute left-1/2 top-[calc(100%+10px)] z-50 w-[300px] -translate-x-1/2 rounded-xl bg-[var(--coachmark-surface)] p-4 text-white shadow-lg"
		>
			{/* Caret centered under the monitor icon (popover is centered on the icon). */}
			<div className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-[var(--coachmark-surface)]" />

			<IconButton
				aria-label="Close"
				onClick={dismiss}
				icon={<XClose className="h-4 w-4" />}
				size="sm"
				color="white"
				className="absolute right-1.5 top-1.5"
			/>

			<div className="flex items-center gap-2 pr-6">
				<span className="text-[12px] font-semibold leading-[16px]">Account overview</span>
				<span className="inline-flex items-center gap-[3px] rounded-[4px] border border-[rgba(133,107,255,0.3)] bg-[rgba(133,107,255,0.1)] px-[6px] py-0 text-[10px] font-medium leading-[16px] text-[#b7b2ff]">
					<Stars02 className="h-2.5 w-2.5" />
					New
				</span>
			</div>

			<p className="mt-2 text-[12px] leading-4 text-[#ededed]">
				Monitor all websites and ad accounts across all workspaces.
			</p>

			<div className="mt-3 flex items-center gap-4">
				<CoachmarkTextButton
					label="Explore"
					tone="primary"
					onClick={() => {
						dismiss()
						onExplore()
					}}
				/>
				<CoachmarkTextButton label="Got it" tone="secondary" onClick={dismiss} />
			</div>
		</div>
	)
}
